import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { IDS } from "@/lib/testIds";
import { useAuth } from "@/lib/auth";
import { ArrowLeft, DownloadSimple, PaperPlaneTilt, Check, X, ArrowClockwise, Trash } from "@phosphor-icons/react";

const STATUS_COLOR = {
  in_progress: "bg-primary text-primary-foreground",
  submitted: "bg-yellow-500 text-black",
  approved: "bg-green-600 text-white",
  rejected: "bg-destructive text-destructive-foreground",
  reopened: "bg-blue-600 text-white",
  draft: "bg-muted text-muted-foreground",
};

export default function SessionDetail() {
  const { sessionId } = useParams();
  const [detail, setDetail] = useState(null);
  const { user } = useAuth();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/inventory/sessions/${sessionId}`);
      setDetail(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [sessionId]);

  useEffect(() => { load(); }, [load]);

  if (!detail) return <div className="p-6 text-sm text-muted-foreground font-mono">Loading…</div>;
  const s = detail.session;
  const stats = detail.stats;
  const canApprove = ["admin", "supervisor"].includes(user?.role);

  const submit = async () => {
    try { await api.post(`/inventory/sessions/${sessionId}/submit`); toast.success("Submitted for approval"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const approve = async (approved) => {
    const reason = approved ? "" : (prompt("Reason for rejection?") || "");
    try { await api.post(`/inventory/sessions/${sessionId}/approve`, { approved, reason }); toast.success(approved ? "Approved" : "Rejected"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const reopen = async () => {
    try { await api.post(`/inventory/sessions/${sessionId}/reopen`); toast.success("Reopened"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const deleteEntry = async (entryId) => {
    if (!window.confirm("Delete this entry?")) return;
    try { await api.delete(`/inventory/sessions/${sessionId}/entries/${entryId}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-4 md:p-6 max-w-6xl">
      <Link to="/sessions" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary mb-3">
        <ArrowLeft size={14} /> All sessions
      </Link>

      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className="label-tech">Session</div>
            <span className={`text-[10px] font-bold tracking-[0.2em] uppercase px-2 py-0.5 rounded-sm ${STATUS_COLOR[s.status]}`}>{s.status.replace("_"," ")}</span>
          </div>
          <h1 className="font-display font-black text-2xl md:text-3xl tracking-tight">{s.name}</h1>
          <div className="text-xs font-mono text-muted-foreground mt-1">{s.session_id} · created by {s.created_by}</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <a
            data-testid={IDS.exportSession}
            href={`${BACKEND_URL}/api/io/export/session/${sessionId}?fmt=xlsx`}
            className="btn-hard flex items-center gap-1 h-10 px-3 border border-border rounded-sm text-xs font-bold uppercase tracking-widest hover:bg-muted"
          ><DownloadSimple size={14} weight="bold" /> Excel</a>
          {s.status === "in_progress" && (
            <button
              data-testid={IDS.submitSession}
              onClick={submit}
              className="btn-hard flex items-center gap-1 h-10 px-3 bg-primary text-primary-foreground rounded-sm text-xs font-bold uppercase tracking-widest hover:bg-orange-600 hover:text-white"
            ><PaperPlaneTilt size={14} weight="bold" /> Submit</button>
          )}
          {s.status === "submitted" && canApprove && (
            <>
              <button
                data-testid={IDS.approveSession}
                onClick={() => approve(true)}
                className="btn-hard flex items-center gap-1 h-10 px-3 bg-green-600 text-white rounded-sm text-xs font-bold uppercase tracking-widest hover:bg-green-700"
              ><Check size={14} weight="bold" /> Approve</button>
              <button
                data-testid={IDS.rejectSession}
                onClick={() => approve(false)}
                className="btn-hard flex items-center gap-1 h-10 px-3 border border-destructive text-destructive rounded-sm text-xs font-bold uppercase tracking-widest hover:bg-destructive hover:text-destructive-foreground"
              ><X size={14} weight="bold" /> Reject</button>
            </>
          )}
          {["approved", "rejected"].includes(s.status) && canApprove && (
            <button
              data-testid={IDS.reopenSession}
              onClick={reopen}
              className="btn-hard flex items-center gap-1 h-10 px-3 border border-border rounded-sm text-xs font-bold uppercase tracking-widest hover:bg-muted"
            ><ArrowClockwise size={14} weight="bold" /> Reopen</button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="card-flat border-border p-3">
          <div className="label-tech mb-1">Entries</div>
          <div className="font-display font-black text-2xl">{stats.entries_count}</div>
        </div>
        <div className="card-flat border-border p-3">
          <div className="label-tech mb-1">Unique items</div>
          <div className="font-display font-black text-2xl">{stats.unique_items}</div>
        </div>
        <div className="card-flat border-border p-3">
          <div className="label-tech mb-1">Total qty</div>
          <div className="font-display font-black text-2xl">{stats.total_qty.toFixed(2)}</div>
        </div>
      </div>

      <div className="card-flat border-border overflow-hidden">
        <div className="p-3 border-b border-border label-tech">Entries</div>
        <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-b border-border sticky top-0 z-10">
              <tr className="text-left label-tech">
                <th className="px-3 py-2">Item</th>
                <th className="px-3 py-2 hidden md:table-cell">Location</th>
                <th className="px-3 py-2 text-right">Qty</th>
                <th className="px-3 py-2">Unit</th>
                <th className="px-3 py-2 text-right">Normalized</th>
                <th className="px-3 py-2 hidden sm:table-cell">Status</th>
                <th className="px-3 py-2 hidden md:table-cell">User</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {detail.entries.map((e) => (
                <tr key={e.entry_id} className="hover:bg-muted/30">
                  <td className="px-3 py-2">
                    <Link to={`/items/${e.variant_id}`} className="font-medium hover:text-primary">{e.product_name}</Link>
                    <div className="text-[10px] font-mono text-muted-foreground">{e.variant_id}</div>
                  </td>
                  <td className="px-3 py-2 hidden md:table-cell text-xs font-mono">{e.location_id}</td>
                  <td className="px-3 py-2 text-right font-mono">
                    {e.original_unit === "mixed"
                      ? `${e.units}u ${e.packs}p ${e.cases}c`
                      : e.original_qty}
                  </td>
                  <td className="px-3 py-2 text-xs uppercase font-mono">{e.original_unit}</td>
                  <td className="px-3 py-2 text-right font-mono font-bold">{e.normalized_qty?.toFixed(2)}</td>
                  <td className="px-3 py-2 hidden sm:table-cell text-xs">{e.status}</td>
                  <td className="px-3 py-2 hidden md:table-cell text-xs">{e.user_email}</td>
                  <td className="px-3 py-2 text-right">
                    {s.status === "in_progress" && (
                      <button onClick={() => deleteEntry(e.entry_id)} className="text-muted-foreground hover:text-destructive">
                        <Trash size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {detail.entries.length === 0 && (
                <tr><td colSpan={8} className="p-8 text-center text-sm text-muted-foreground">
                  No entries yet. <Link to="/scan" className="text-primary underline">Scan an item</Link> to begin.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
